import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStoresApi } from '@/features/wizard';
import type { ICouponDetails } from '@/types';
import { updateAuctionHouse } from '../store';
import { useLocation, useMatch } from 'react-router-dom';

interface IUseQuotePrefill {
  isLoading: boolean;
}

export const useQuotePrefill = (): IUseQuotePrefill => {
  const isOnQuotePage = useMatch('/quote');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const storesApi = useStoresApi();
  const location = useLocation();

  // Use snake_case in public URLs for better readability and convention consistency
  const couponCode = new URLSearchParams(location.search).get('coupon_code');

  const fetchStoreByCouponCode = useCallback(
    async (couponCode: string) => {
      if (!couponCode) {
        return;
      }

      try {
        setIsLoading(true);
        const stores = await storesApi.getStoreByCouponCode(couponCode, true);

        return stores.length > 0 ? stores[0].couponDetails : undefined;
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [storesApi]
  );

  const prefillWithCouponDetails = useCallback(
    (details: ICouponDetails) => {
      const { auctionHouse, code } = details;

      // TODO: Investigate prefill issues for logged-in customers.

      dispatch(
        updateAuctionHouse({
          name: auctionHouse.name,
          hasItemData: false,
          myFile: [],
          auctionCode: '',
          auctionDate: '',
          batchCode: '',
          batchValue: '',
          city: auctionHouse.city,
          countryCode: auctionHouse.countryCode,
          province: auctionHouse.province,
          streetAddress: auctionHouse.streetAddress,
          zipCode: auctionHouse.zipCode,
          couponCode: code,
        })
      );
    },
    [dispatch]
  );

  const runPrefill = useCallback(async () => {
    if (!isOnQuotePage || !couponCode) {
      return;
    }

    try {
      const couponDetails = await fetchStoreByCouponCode(couponCode);

      if (couponDetails) {
        prefillWithCouponDetails(couponDetails);
      }
    } catch (error) {
      console.error('Error during form prefill:', error);
    }
  }, [couponCode, fetchStoreByCouponCode, isOnQuotePage, prefillWithCouponDetails]);

  useEffect(() => {
    void runPrefill();
  }, [runPrefill]);

  return { isLoading: isOnQuotePage ? isLoading : false };
};
